import React, { useEffect, useState } from 'react';

import { Button, Segment, Image, Container, Message } from 'semantic-ui-react';

import _get from 'lodash/get';
import { useSelector } from 'react-redux';

import {
  getGoogleLocationProviderSettings,
  getGoogleProfileProviderSetting,
  getGoogleLocationProviderSetting,
} from 'components/modules/business-profile/services/provider-service';
import useProviderServiceSettings from 'components/modules/integrations/hooks/use-provider-service-settings';
import { objectToSnakeCase } from 'libs/format';

import { GMB_AUTH_ERROR_KEY } from './ProfilePanel.constants';
import {
  hasGoogleMyBusinessToken,
  getErrorHeaderAndMessage,
  getGoogleMyBusinessAccountId,
  getLocationSettingsChangePayload,
  getCurrentStep,
  getAssociatedLocations,
  getConnectionSteps,
  getAccountSelectPayload,
  dedupeGoogleLocations,
} from './ProfilePanel.utils';
import API from '../../../../../../libs/api';
import { isAuthorizedForSaleViews } from '../../../../../../libs/auth';
import ErrorHandler, { getErrorMessage } from '../../../../../../libs/errors';
import {
  selectBusinessId,
  selectBusinessType,
  selectUserRoleForBusiness,
} from '../../../../../../selectors/business';
import useAsyncEffect from '../../../../foundation/hooks/use-async-effect';
import useBranding from '../../../../foundation/hooks/use-branding';
import AccountList from '../AccountList';
import AccountLocationList from '../AccountLocationList';
import AccountSettings from '../AccountSettings';
import ConnectionSteps from '../ConnectionSteps';

import GMBLogo from '../../../../../../static/logos/GMB-logo.png';

import './ProfilePanel.scss';

export default function ProfilePanel() {
  const businessId = useSelector(selectBusinessId);
  const businessType = useSelector(selectBusinessType);
  const userRole = useSelector(selectUserRoleForBusiness);

  const { providerServiceSettings, refreshSettings, updateSetting } = useProviderServiceSettings();

  const { brandName } = useBranding();

  const [accounts, setAccounts] = useState(null);
  const [locations, setLocations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [disconnectLocationError, setDisconnectLocationError] = useState(null);

  const currentStep = getCurrentStep(providerServiceSettings);
  const selectedGmbAccountId = getGoogleMyBusinessAccountId(providerServiceSettings);

  async function refreshGbp({ gmbAccountId } = {}) {
    try {
      setLoading(true);
      setError(null);

      if (!hasGoogleMyBusinessToken(providerServiceSettings)) {
        setAccounts(null);
        setLocations(null);
        return;
      }

      const newAccounts = await API.getGoogleMyBusinessAccounts(businessType, businessId);
      setAccounts(newAccounts);

      const selectedAccountId =
        gmbAccountId || getGoogleMyBusinessAccountId(providerServiceSettings);
      if (!selectedAccountId) {
        setLocations(null);
        return;
      }

      const accountLocations = await API.getGoogleMyBusinessLocations(
        businessType,
        businessId,
        selectedAccountId,
      );
      setLocations(dedupeGoogleLocations(accountLocations));
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
    } finally {
      setLoading(false);
    }
  }

  async function onAccountSelect(data) {
    try {
      setLoading(true);
      const serviceSetting = getGoogleProfileProviderSetting(providerServiceSettings);
      const payload = getAccountSelectPayload(serviceSetting, data);
      await updateSetting(serviceSetting.id, payload);
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
    } finally {
      setLoading(false);
    }
  }

  async function onLocationsAssociation(data) {
    try {
      setLoading(true);
      const payload = objectToSnakeCase(data);
      await API.addGoogleMyBusinessLocation(businessType, businessId, payload);
      await refreshSettings();
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
    } finally {
      setLoading(false);
    }
  }

  async function onDisconnectLocation(internalLocationId) {
    try {
      setLoading(true);
      setDisconnectLocationError(null);
      await API.disconnectGoogleMyBusinessLocation(businessType, businessId, internalLocationId);
      await refreshSettings();
      await refreshGbp();
    } catch (err) {
      setDisconnectLocationError(err);
      ErrorHandler.capture(err);
    } finally {
      setLoading(false);
    }
  }

  async function onDisconnect() {
    try {
      setLoading(true);
      await API.disconnectGoogleMyBusiness(businessType, businessId);
      await refreshSettings();
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
    } finally {
      setLoading(false);
    }
  }

  async function onLocationSettingsChange(associatedLocation, newSyncSettings) {
    try {
      setLoading(true);
      const locationSettings = getGoogleLocationProviderSettings(providerServiceSettings);
      const serviceSetting = getGoogleLocationProviderSetting(locationSettings, associatedLocation);
      const { id: serviceId } = serviceSetting;
      const payload = getLocationSettingsChangePayload(serviceSetting, newSyncSettings);
      await updateSetting(serviceId, payload);
      setLoading(false);
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
      setLoading(false);
    }
  }

  async function getGoogleMyBusinessAuth() {
    try {
      setLoading(true);
      const response = await API.getGoogleMyBusinessAuth(businessType, businessId);
      if (response.data.url) {
        window.location.assign(response.data.url);
      }
    } catch (err) {
      setError(err);
      ErrorHandler.capture(err);
    } finally {
      setLoading(false);
    }
  }

  useAsyncEffect(async () => {
    await refreshSettings();
    await refreshGbp();
  }, []);

  useEffect(() => {
    if (!hasGoogleMyBusinessToken(providerServiceSettings) || !selectedGmbAccountId) {
      return;
    }
    refreshGbp({ gmbAccountId: selectedGmbAccountId });
  }, [selectedGmbAccountId, businessId, businessType]);

  // Step renderers
  function renderAuthorize() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    let authError = null;
    if (urlSearchParams.has(GMB_AUTH_ERROR_KEY)) {
      const [authErrorHeader, authErrorMsg] = getErrorHeaderAndMessage(
        urlSearchParams.get(GMB_AUTH_ERROR_KEY),
        brandName,
      );
      authError = <Message negative header={authErrorHeader} content={authErrorMsg} />;
    }
    return (
      <>
        <Segment attached="bottom">
          {authError}
          <Message>
            <Message.Header>Connect {brandName} to Google</Message.Header>
            <p>
              By connecting your {brandName} website to Google, we can automatically update critical
              information about your business as you update it. To enable this functionality, you
              will need to authorize {brandName} to manage your business listing.
            </p>
          </Message>
          <Container textAlign="center">
            <Button size="big" primary onClick={getGoogleMyBusinessAuth} loading={loading}>
              Authorize {brandName}
            </Button>
          </Container>
        </Segment>
        {error && (
          <Segment attached="bottom">
            <Message negative>
              <Message.Header>Issue Configuring Google Business Profile</Message.Header>
              <p>{error.message || 'There was an error, please try again later.'}</p>
            </Message>
          </Segment>
        )}
      </>
    );
  }

  function renderSelectAccount() {
    if (loading) return <Segment attached="bottom" padded="very" loading />;
    return <AccountList accounts={accounts} onAccountSelect={onAccountSelect} />;
  }

  function renderSelectLocation() {
    if (loading) {
      return <Segment attached="bottom" padded="very" loading />;
    }
    if (locations === null) {
      if (error) {
        return (
          <Segment attached="bottom">
            <Message negative>
              <Message.Header>Could not load Google locations</Message.Header>
              <p>{error.message || 'There was an error, please try again later.'}</p>
            </Message>
            <Container textAlign="center">
              <Button primary content="Try again" onClick={() => refreshGbp()} />
            </Container>
          </Segment>
        );
      }
      return <Segment attached="bottom" padded="very" loading />;
    }
    return (
      <AccountLocationList
        gmbAccountId={getGoogleMyBusinessAccountId(providerServiceSettings)}
        gmbLocations={locations}
        onDisconnect={onDisconnect}
        onLocationsAssociation={onLocationsAssociation}
      />
    );
  }

  function renderAccountSettings() {
    const locationSettings = getGoogleLocationProviderSettings(providerServiceSettings);
    const associatedLocations = getAssociatedLocations(locations, locationSettings);

    return (
      <>
        {disconnectLocationError && (
          <Message
            negative
            content={
              _get(disconnectLocationError, 'response.status') === 403
                ? 'Staff only'
                : getErrorMessage(
                    disconnectLocationError,
                    'Could not disconnect this Google link. Please try again.',
                  )
            }
          />
        )}
        <AccountSettings
          loading={loading}
          associatedLocations={associatedLocations}
          locationSettings={locationSettings}
          onDisconnect={onDisconnect}
          onDisconnectLocation={onDisconnectLocation}
          onLocationSettingsChange={onLocationSettingsChange}
        />
      </>
    );
  }

  function renderIntro() {
    if (currentStep > 3) return null;
    return (
      <Segment attached="top">
        <Container textAlign="center">
          <Image src={GMBLogo} style={{ display: 'inline', margin: 0, height: '100px' }} />
        </Container>
        <Container>
          <p>
            <b>Unlock Your Online Visibility with Google Business Profile! </b>
            This powerful, free tool empowers businesses and organizations to take control of their
            Google presence, spanning Search and Maps. By verifying and refining your business
            details, you will not only make it easy for customers to discover you but also share the
            unique qualities that make your business special.
          </p>
        </Container>
      </Segment>
    );
  }

  if (isAuthorizedForSaleViews(userRole)) return null;

  return (
    <div className="google-business-profile-panel">
      {renderIntro()}
      <Segment attached>
        <ConnectionSteps steps={getConnectionSteps(currentStep, brandName)} />
      </Segment>
      {(() => {
        switch (currentStep) {
          case 1:
            return renderAuthorize();
          case 2:
            return renderSelectAccount();
          case 3:
            return renderSelectLocation();
          case 4:
            return renderAccountSettings();
          default:
            return null;
        }
      })()}
    </div>
  );
}
